import {Provider} from '@angular/core';
import {CanMatchFn} from '@angular/router';
import {of, throwError} from 'rxjs';

import {ConnectNodesService, type ConnectNodeCatalog, type ConnectWorkflowNodePaletteItem} from '../../../services/connect/connect-nodes.service';
import {type ConnectProviderCatalog, ConnectProvidersService} from '../../../services/connect/connect-providers.service';
import {ConnectWorkflowsService, type ConnectWorkflow, type ConnectWorkflowRun} from '../../../services/connect/connect-workflows.service';
import {CONNECT_WORKFLOW_CORE_NODE_TYPES} from './connect-workflow-node-manifest';

const paletteReviewFixtureState = 'palette-review';
const paletteReviewFailureFixtureState = 'palette-review-failure';
const allowedFixtureStates = new Set([paletteReviewFixtureState, paletteReviewFailureFixtureState]);
const allowedFixturePaths = new Set(['/connect/workflows/new']);
const localhostHosts = new Set(['localhost', '127.0.0.1', '[::1]', '::1']);
export const CONNECT_WORKFLOW_VISUAL_FIXTURE_ID = 'fixture-workflow';

const CONNECT_WORKFLOW_FIXTURE_PROVIDER_CATALOG: ConnectProviderCatalog = {
  version: 'v1',
  providers: [
    {
      id: 'hubspot',
      name: 'HubSpot',
      ref: 'hubspot.oauth',
      description: 'Fixture CRM provider group for workflow palette review.',
      auth: {supported: ['oauth'], default: 'oauth'},
      palette: {
        color: '#ea580c',
        background: '#fff7ed',
        border: '#fdba74',
        text: '#9a3412',
      },
    },
    {
      id: 'slack',
      name: 'Slack',
      ref: 'slack.oauth',
      description: 'Fixture messaging provider group for workflow palette review.',
      auth: {supported: ['oauth'], default: 'oauth'},
      palette: {
        color: '#7c3aed',
        background: '#f5f3ff',
        border: '#c4b5fd',
        text: '#5b21b6',
      },
    },
  ],
};

const CONNECT_WORKFLOW_FIXTURE_PROVIDER_NODES: ConnectWorkflowNodePaletteItem[] = [
  {
    paletteId: 'hubspot.companies',
    workflowType: 'api.connector',
    label: 'Companies',
    description: 'Fixture HubSpot companies actions for provider grouping review.',
    category: 'Integrations',
    providerId: 'hubspot',
    actions: ['get', 'list', 'search', 'upsert'],
    defaults: {ref: 'hubspot.companies.get'},
  },
  {
    paletteId: 'hubspot.contacts',
    workflowType: 'api.connector',
    label: 'Contacts',
    description: 'Fixture HubSpot contacts actions for provider grouping review.',
    category: 'Integrations',
    providerId: 'hubspot',
    actions: ['get', 'list', 'search', 'upsert'],
    defaults: {ref: 'hubspot.contacts.get'},
  },
  {
    paletteId: 'hubspot.custom',
    workflowType: 'api.connector',
    label: 'Custom objects',
    description: 'Fixture HubSpot custom object actions for provider grouping review.',
    category: 'Integrations',
    providerId: 'hubspot',
    actions: ['get', 'list', 'search', 'upsert'],
    defaults: {ref: 'hubspot.custom.get'},
  },
  {
    paletteId: 'hubspot.deals',
    workflowType: 'api.connector',
    label: 'Deals',
    description: 'Fixture HubSpot deal actions for provider grouping review.',
    category: 'Integrations',
    providerId: 'hubspot',
    actions: ['get', 'list', 'search', 'upsert'],
    defaults: {ref: 'hubspot.deals.get'},
  },
  {
    paletteId: 'hubspot.other',
    workflowType: 'api.connector',
    label: 'Other objects',
    description: 'Fixture HubSpot other object actions for provider grouping review.',
    category: 'Integrations',
    providerId: 'hubspot',
    actions: ['get', 'list', 'search', 'upsert'],
    defaults: {ref: 'hubspot.other.get'},
  },
  {
    paletteId: 'hubspot.owners',
    workflowType: 'api.connector',
    label: 'Owners',
    description: 'Fixture HubSpot owner actions for provider grouping review.',
    category: 'Integrations',
    providerId: 'hubspot',
    actions: ['list'],
    defaults: {ref: 'hubspot.owners.list'},
  },
  {
    paletteId: 'slack.channels',
    workflowType: 'api.connector',
    label: 'Channels',
    description: 'Fixture Slack channel lookup actions for provider grouping review.',
    category: 'Integrations',
    providerId: 'slack',
    actions: ['get', 'list', 'search'],
    defaults: {ref: 'slack.channels.list'},
  },
  {
    paletteId: 'slack.messages',
    workflowType: 'api.connector',
    label: 'Messages',
    description: 'Fixture Slack message actions for provider grouping review.',
    category: 'Integrations',
    providerId: 'slack',
    actions: ['create', 'list'],
    defaults: {ref: 'slack.messages.create'},
  },
];

const CONNECT_WORKFLOW_FIXTURE_NODE_CATALOG: ConnectNodeCatalog = {
  version: 'v1',
  nodes: [...CONNECT_WORKFLOW_CORE_NODE_TYPES, ...CONNECT_WORKFLOW_FIXTURE_PROVIDER_NODES],
};

function withFixtureWorkflowId(workflow: ConnectWorkflow, workflowId?: string): ConnectWorkflow {
  const id = String(workflowId || workflow.id || '').trim() || CONNECT_WORKFLOW_VISUAL_FIXTURE_ID;

  return {
    ...workflow,
    id,
  };
}

export function isConnectWorkflowVisualFixtureUrl(href: string): boolean {
  const url = new URL(href);
  const fixtureState = url.searchParams.get('connectWorkflowFixture') || '';

  return localhostHosts.has(url.hostname) && allowedFixturePaths.has(url.pathname) && allowedFixtureStates.has(fixtureState);
}

function getConnectWorkflowVisualFixtureState(): string {
  if (typeof window === 'undefined' || !window.location) return '';

  return new URL(window.location.href).searchParams.get('connectWorkflowFixture') || '';
}

export const connectWorkflowVisualFixtureCanMatch: CanMatchFn = () => {
  if (typeof window === 'undefined' || !window.location) return false;

  return isConnectWorkflowVisualFixtureUrl(window.location.href);
};

export const connectWorkflowVisualFixtureProviders: Provider[] = [
  {
    provide: ConnectProvidersService,
    useValue: {
      fetchCatalog: () => of(CONNECT_WORKFLOW_FIXTURE_PROVIDER_CATALOG),
      updateCatalog: (catalog: ConnectProviderCatalog) => of(catalog),
      getUtilities: () => of([]),
      getUtilityItems: () => of({items: []}),
    },
  },
  {
    provide: ConnectNodesService,
    useValue: {
      fetchCatalog: () => getConnectWorkflowVisualFixtureState() === paletteReviewFailureFixtureState
        ? throwError(() => new Error('Synthetic workflow palette fixture catalog failure'))
        : of(CONNECT_WORKFLOW_FIXTURE_NODE_CATALOG),
    },
  },
  {
    provide: ConnectWorkflowsService,
    useValue: {
      getWorkflow: () => of(null),
      createWorkflow: (workflow: ConnectWorkflow) => of(withFixtureWorkflowId(workflow)),
      updateWorkflow: (workflowId: string, workflow: ConnectWorkflow) => of(withFixtureWorkflowId(workflow, workflowId)),
      runWorkflow: (workflowId: string) =>
        of({
          runId: 'fixture-run-001',
          workflowId,
          status: 'queued',
          enqueuedAt: '2026-07-01T00:00:00Z',
        } satisfies ConnectWorkflowRun),
      getRun: () =>
        of({
          id: 'fixture-run-001',
          workflow_id: 'fixture-workflow',
          status: 'queued',
          created_at: '2026-07-01T00:00:00Z',
          steps: [],
        }),
    },
  },
];
